import type { RouterIds } from '@/Router';
import { useBreakpoints } from '@/Utility/hooks';
import { Stack, type StackProps } from '@mui/material';
import React, { createContext, useContext, useMemo } from 'react';
import { z } from 'zod';
import { filterRegistry } from './registry';
import { __useFilterBarInit } from './store';

import './FilterBar.less';

/**
 * Types for filter configuration objects
 */
export type FilterConfigObject = {
  type: keyof typeof filterRegistry;
  id?: string;
  [key: string]: string | number | boolean | undefined;
};

export type CustomFilterConfig = {
  type: 'custom';
  id?: string;
  component: React.ReactNode;
};

export type FilterConfig = FilterConfigObject | CustomFilterConfig | null | undefined | false;

/**
 * Props for each section of filters (left, center, right)
 */
export interface FilterSectionProps {
  configs?: FilterConfig[];
  stackProps?: StackProps;
}

type RouteIdOrStorageKey = { routeId: RouterIds | `key:${string}` };

export type { RouteIdOrStorageKey as FilterIds };

type RouteIdOrStorageKeyOptions = {
  options?: {
    /**
     * Restore filters from local storage when the filter bar is mounted
     */
    restoreOnMount: boolean;
    /**
     * Precedence of URL parameters over local storage
     * When url mode is active, we can choose to restore the filters using either
     * - url: prioritize URL parameters
     * - local: prioritize local storage
     * - merge: merge URL parameters and local storage
     *
     * @default 'url'
     */
    urlParamsPrecedence?: 'url' | 'local' | 'merge';
  };
};

export type FilterRouteConfig = RouteIdOrStorageKey & RouteIdOrStorageKeyOptions;

/**
 * Props for the FilterBar component
 */
type FilterBarCtxProps = {
  variant?: 'default' | 'compact';
};

export type FilterBarProps = RouteIdOrStorageKey &
  FilterBarCtxProps &
  RouteIdOrStorageKeyOptions & {
    left?: FilterSectionProps;
    center?: FilterSectionProps;
    right?: FilterSectionProps;
    sticky?: boolean;
    rootProps?: StackProps;
  };

/**
 * check if an item is a custom config
 */
const isCustomConfig = (config: FilterConfig): config is CustomFilterConfig => {
  return config !== null && config !== undefined && config !== false && config.type === 'custom';
};

/**
 * check if an item is a filter config object
 */
const isFilterConfigObject = (config: FilterConfig): config is FilterConfigObject => {
  return config !== null && config !== undefined && config !== false && config.type !== 'custom';
};

/**
 * Generate a key for a filter item
 */
const getFilterKey = (config: FilterConfig, position: number): string => {
  // Handle null, undefined, or false configs
  if (!config) {
    return `empty-${position}`;
  }

  if ('id' in config && config.id) {
    return config.id;
  }

  if (isCustomConfig(config)) {
    return `custom-${position}`;
  }

  if (isFilterConfigObject(config)) {
    if ('name' in config && typeof config.name === 'string') {
      return `${config.type}-${config.name}`;
    }

    if ('displayName' in config && typeof config.displayName === 'string') {
      return `${config.type}-${config.displayName}`;
    }

    // Fallback to type and position
    return `${config.type}-${position}`;
  }

  return `filter-${position}`;
};

/**
 * Interface for FilterSection component props
 */
interface FilterSectionComponentProps {
  configs?: FilterConfig[];
  stackProps?: StackProps;
  className: string;
  justifyContent?: string;
}

/**
 * Memoized component for a section of filters
 * Prevents unnecessary re-renders when other sections change
 */
const FilterSection = React.memo(
  ({ configs = [], stackProps, className, justifyContent }: FilterSectionComponentProps) => {
    // Generate filter items from configurations
    const filterItems = useMemo(() => {
      return configs
        .filter(Boolean)
        .map((config, position) => {
          if (!config) return null;

          const key = getFilterKey(config, position);

          if (isCustomConfig(config)) {
            return <React.Fragment key={key}>{config.component}</React.Fragment>;
          }

          if (!isFilterConfigObject(config)) return null;

          const { type, id, ...props } = config;

          // Get the component from the registry
          const filterDefinition = filterRegistry[type];

          const FilterComponent = filterDefinition.component;

          return <FilterComponent key={key} {...props} />;
        })
        .filter(Boolean);
    }, [configs]);

    if (filterItems.length === 0) return null;

    return (
      <Stack
        className={className}
        direction="row"
        gap={0.5}
        flexWrap="wrap"
        justifyContent={justifyContent}
        {...stackProps}
      >
        {filterItems}
      </Stack>
    );
  }
);

const FilterBar: React.FC<FilterBarProps> = React.memo(
  ({ left, center, right, sticky = false, rootProps, variant }) => {
    const { isMobile } = useBreakpoints();

    const { hasCenterItems, direction, rootClassName } = useMemo(() => {
      const hasCenterItems = center?.configs && center.configs.length > 0;
      const direction: StackProps['direction'] = isMobile ? 'column' : 'row';
      const compactbar = variant === 'compact';

      const rootClassName = `FilterBar ${sticky ? 'sticky' : ''} ${hasCenterItems ? 'centered' : ''} ${
        compactbar ? 'FilterBar--compact' : ''
      }`;
      return { hasCenterItems, direction, rootClassName };
    }, [center, isMobile, sticky, variant]);

    return (
      <Stack className={rootClassName} direction={direction} flexWrap="wrap" {...rootProps}>
        <FilterSection
          configs={left?.configs}
          stackProps={left?.stackProps}
          className="left"
          justifyContent={isMobile ? 'center' : undefined}
        />

        {hasCenterItems && !isMobile && (
          <FilterSection configs={center?.configs} stackProps={center?.stackProps} className="center" />
        )}

        <FilterSection
          configs={right?.configs}
          stackProps={right?.stackProps}
          className="right"
          justifyContent="space-between"
        />
      </Stack>
    );
  }
);

type FilterBarContextType = RouteIdOrStorageKeyOptions & {
  schema: z.ZodObject<z.ZodRawShape>;
  props: FilterBarCtxProps;
} & (
    | {
        mode: 'routeId';
        id: RouterIds;
      }
    | {
        mode: 'storageKey';
        id: string;
      }
  );

const FilterBarContext = createContext<FilterBarContextType | null>(null);

export const useFilterBarCtx = () => {
  const ctx = useContext(FilterBarContext);
  if (!ctx) {
    throw new Error('useFilterBarCtx must be used within a FilterBarProvider');
  }
  return ctx;
};

const FilterBarWrapper = (props: FilterBarProps) => {
  // compute the schema for the filter bar based on the configs that are passed in
  const schema = useMemo(() => {
    const configs = [props?.left?.configs, props?.center?.configs, props?.right?.configs]
      .flat()
      .filter((item) => !!item && item.type !== 'custom')
      .map((item) => {
        return item.type;
      });

    let shapes = {};

    configs.forEach((config) => {
      const filterDefinition = filterRegistry[config]?.searchSchema;

      if (filterDefinition instanceof z.ZodType) {
        shapes = {
          ...shapes,
          ...filterDefinition.shape,
        };
      }
    });

    return z.object(shapes);
  }, [props?.left?.configs, props?.center?.configs, props?.right?.configs]);

  const ctxValue: FilterBarContextType = useMemo(() => {
    const { options } = props;

    const isStorageKey = props.routeId.startsWith('key:');

    return {
      props: {
        variant: props.variant ?? 'default',
      },
      options: {
        ...options,
        restoreOnMount: options?.restoreOnMount || false,
        urlParamsPrecedence: options?.urlParamsPrecedence || (isStorageKey ? 'local' : 'url'),
      },
      schema: schema,
      ...(isStorageKey
        ? {
            id: props.routeId as `key:${string}`,
            mode: 'storageKey',
          }
        : {
            id: props.routeId as RouterIds,
            mode: 'routeId',
          }),
    };
  }, [props, schema]);

  __useFilterBarInit(
    {
      routeId: props.routeId,
      options: props?.options,
    },
    schema
  );

  return (
    <FilterBarContext.Provider value={ctxValue}>
      <FilterBar {...props} />
    </FilterBarContext.Provider>
  );
};

const FilterBarWrapperMemo = React.memo(FilterBarWrapper);

export { FilterBarWrapperMemo as FilterBar };
